// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../config';
import session from '../../../session';
import alert from '../../../alert';
import loading from '../../../components/vue/ProcessingIcon';
import maintenance from '../../../components/vue/MaintenanceError';
import util from '../../../util/utils';
import resource from '../../../util/resourceMap';
import saml from '../../../util/saml';
import errorMapping from '../../../util/api/errorMapping';
import errors from '@/common/nodejs/errors';

export default {
  data() {
    return {
      processing: false,
      showMaintenanceScreen: false,
      displayLoginScreen: false,
      ssoDisplayLanguage: '',
      isForcedLogin: false,
      redirectSkip: false,
      isManage: false,
      errorAccountList: [],
      isCloudfrontLoginPage: process.env.VUE_APP_NEEDS_SSO_SELECTION ? true : false
    };
  },

  components: {
    'processing-view': loading,
    'maintenance-screen': maintenance
  },

  computed: {
    ...mapState({
      languageList: state => state.fileResources.languages,
      language: state => state.systems.language,
      isMaintenance: state => state.systems.isMaintenance,
      samlAccountList: state => state.systems.samlAccountList,
      securityNumber: state => state.systems.securityNumber,
      isSkipStart: state => state.app.isSkipStart,
      icpNumber: state => state.systems.icpNumber,
      currentProvider: state => state.provider.currentProvider,
      cloudConnectorId: state => state.app.cloudConnectorId,
      isExternalError: state => state.systems.isExternalError
    })
  },

  watch: {
    language(value) {
      this.samlAccountList.forEach((list) => {
        list.name = list.names[value];
      });
      const switchSsoLanguage = resource.ssoLanguageDisplay(value, this.currentProvider);
      if (switchSsoLanguage) {
        this.ssoDisplayLanguage = switchSsoLanguage.ssoDisplayLanguage;
      }
    },
  },

  methods: {
    ...mapActions('app', ['setHeaderTitleID', 'setIsSkipStart', 'setInitLogin', 'setQueryParamCCID', 'setDirectMove']),
    ...mapActions('auth', ['logout', 'generateCodeVerifier', 'generateCodeChallenge']),
    ...mapActions('systems', ['setLanguage', 'setSystemVersion', "getSystemInfo", "setExternalError", "setExternalEndpointList"]),
    ...mapActions('provider', ['setCurrentProvider']),

    // メンテナンス中画面ログイン
    executeForcedLogin: function () {
      this.showMaintenanceScreen = false;
      const type = this.isManage ? config.SERVICE_MANAGE : config.SERVICE_REPRO;
      if (util.isChinaResion) {
        // system情報取得用パラメータ 
        const param = {
          type: type,
          language: this.language,
          domain: ''
        };
        this.setExternalEndpointList(param);
      }
      this.isForcedLogin = true;
      this.init();
    },

    // eslint-disable-next-line complexity
    redirectToIdpAdLogin: async function (samlAccount) {
      const lang = util.getWindowNavigatorLanguage(config.LANGUAGE_LIST); // ログイン画面の表示言語
      let baseUrl = `${samlAccount.url}&redirect_uri=${window.location.origin}`;
      if (this.isManage) {
        baseUrl += "/manage/";
      }

      // PKCE対応
      // code_verifierを生成
      const codeVerifier = await this.generateCodeVerifier();

      // code_challengeを生成
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);

      if (codeVerifier && codeChallenge) {
        baseUrl += `&code_challenge_method=S256&code_challenge=${codeChallenge}`;
      }

      if (samlAccount.isIDP) {
        // IDP連携のSignin
        if (!samlAccount.isAutoRedirectLogin) {
          baseUrl += `&lang=${lang}`;
        } else {
          baseUrl += `&lang=${lang}&identity_provider=${samlAccount.provider}`;
        }
      } else {
        baseUrl += `&lang=${lang}&identity_provider=${samlAccount.provider}`;
      }

      // ダイレクト連携のために、必要なら%state=に遷移する情報を追加する
      baseUrl += this.setStateUrl(samlAccount);

      this.setExternalError(false);

      window.location.href = baseUrl;
      // url例：https://auth.idp.subaru.co.jp/oauth2/authorize?response_type=code&client_id=xxxxx&redirect_uri=https://techsupport.subaru.co.jp&code_challenge_method=S256&code_challenge=yyyyy&lang=ja
    },

    // 中国環境のために残してある（通常環境は表示されない）
    startSkipChecked(value) {
      this.setIsSkipStart(value);
    },

    directCheck(states) {
      let directMove = {};
      if (states[0] === "menu" || states[0] === "soldVehicleSystemTable") {
        directMove = { direct_name: states[0] };
      } else if (states[0] === "systemInformation") {
        directMove = {
          direct_name: states[0],
          unit_id: states[1],
          pfc_id: states[2]
        };
      }
      this.setDirectMove(directMove);
    },

    setStates(states) {
      let isSkip = false;
      if (this.isManage) {
        if (states.length === 3) {
          // リプログラミングファイル詳細画面
          const directMove = {
            direct_name: states[0],
            file_id: states[1]
          };
          // リプログラミングファイル詳細画面のページとID情報を設定する
          this.setDirectMove(directMove);
        } else if (states.length === 2) {
          // CANID一覧画面
          const directMove = {
            direct_name: states[0]
          };
          // CANID一覧画面のページ情報を設定する
          this.setDirectMove(directMove);
        }
      } else {
        // CCからSSM5を起動したときのみに通る処理
        if (states.length === 3) {
          isSkip = (states[0] === 'true') ? true : false;
          const ccid = states[1];
          this.setQueryParamCCID(ccid);
        }
        // skip情報を設定する。 
        this.setIsSkipStart(isSkip);
      }
    },

    async init() {
      const hostname = window.location.hostname;

      // ダイレクト連携でBlankの次に遷移する画面を設定する
      if (!this.isManage && Object.prototype.hasOwnProperty.call(this.$route.query, 'state')) {
        const states = this.$route.query['state'].split('_');
        this.directCheck(states);
      }

      // 通常ログイン画面の場合
      // SSM5 バージョン情報取得
      this.processing = true;
      await this.getSystemInfo();

      // アカウント取得用パラメータ 
      const type = this.isManage ? config.SERVICE_MANAGE : config.SERVICE_REPRO;
      const param = {
        type: type,
        language: this.language,
        domain: hostname
      };
      // SAMLアカウントリスト取得
      let result = true;
      result = await this.setExternalEndpointList(param);
      if (!result) {
        // 取得に失敗したら成功するまで取得し続ける
        alert.showError(this, null, this.$t(errorMapping.getErrorMessageForCode(errors.INVALID_INPUT_MISSING_PARAM)), () => {
          this.init();
        });
      }
      this.processing = false;

      if (!util.isChinaResion() || !Object.prototype.hasOwnProperty.call(this.$route.query, 'code')) {
        // メンテナンス中ならメンテナンス画面を表示
        // メンテナンス中にコマンドが実行された場合はメンテナンス状態をfalseにする
        this.showMaintenanceScreen = this.isForcedLogin ? false : this.isMaintenance;
        if (this.isMaintenance && !this.isForcedLogin) return;
      }
      if (this.$route.query) {
        if (Object.prototype.hasOwnProperty.call(this.$route.query, 'code')) {
          const code = this.$route.query['code'];
          if (Object.prototype.hasOwnProperty.call(this.$route.query, 'state')) {
            // redirect_uriのstateにexternalAuthTestが設定されている場合のみに通る処理(cognitoのUIから起動の場合の動作確認用処理)
            if (this.$route.query['state'] === config.STATE_EXTERNAL_AUTH_TEST) {
              this.displayLoginScreen = true;
              return;
            }
            const states = this.$route.query['state'].split('_');

            this.setStates(states);
          }

          // Blank 画面に遷移させる
          this.setInitLogin(true);
          this.$router.push({ name: 'blank', params: { code: code, domain: hostname } });
          return;
        }
        else if (Object.prototype.hasOwnProperty.call(this.$route.query, 'error_description') || Object.prototype.hasOwnProperty.call(this.$route.query, 'error')) {
          // SAML連携エラーの場合
          this.setExternalError(true);
          // エラー時のログイン画面では、サインインしようとしていたユーザーのプロバイダー情報のみに適したログイン画面を表示する
          this.errorAccountList = saml.extractSamlAccount(this.samlAccountList, this.currentProvider);
          return;
        }
        else if (Object.prototype.hasOwnProperty.call(this.$route.query, 'ccid')) {
          // クラウドコネクターIDが設定されていた場合
          const ccid = this.$route.query['ccid'];
          this.setQueryParamCCID(ccid);
        }
      }
      this.errorAccountList = saml.extractSamlAccount(this.samlAccountList, this.currentProvider);

      // Start画面のskipを実施を無効にする。 
      this.setIsSkipStart(false);

      // SUBARUデータ登録者、技本ユーザー、SCI連携ログインの場合、IDP画面に遷移する
      if (!this.redirectSkip) {
        this.processing = true;
        this.redirectToIdpAdLogin(saml.extractSamlAccount(this.samlAccountList, this.currentProvider));
        this.processing = false;
      }
    },

    /**
    * ラベルの表示文言切り替え
    */
    labelLoginMessage() {
      const providerLabels = {
        [config.SCI_DOMAIN]: {
          labelLoginTop: this.$t('message.label_signin_other_system_sci'),
          labelLoginBottom: this.$t('message.label_signin_other_system_under_sci'),
        },
        [config.SOA_DOMAIN]: {
          labelLoginTop: this.$t('message.label_signin_other_system_soa'),
          labelLoginBottom: '',
        },
        [config.THIRD_PARTY_DOMAIN]: {
          labelLoginTop: this.$t('message.label_signin_other_system_third_party'),
          labelLoginBottom: this.$t('message.label_signin_other_system_under_third_party'),
        }
      };

      // デフォルトのラベル（SUBARU）
      const defaultLabels = {
        labelLoginTop: this.$t('message.label_signin_other_system_idp'),
        labelLoginBottom: this.$t('message.label_signin_other_system_under')
      };
      // currentProvider に対応するラベルがあればそれを、なければデフォルトを返す
      return providerLabels[this.currentProvider] || defaultLabels;
    },

    setStateUrl(samlAccount) {
      let stateUrl = "";
      if (this.isManage) {
        let prames = null;
        if (this.$route.query && this.$route.query.direct_name && this.$route.query.file_id) {
          // リプログラミングファイル詳細画面
          prames = `${this.$route.query.direct_name}_${this.$route.query.file_id}`;
        } else if (this.$route.query && this.$route.query.direct_name) {
          // CANID一覧画面
          prames = `${this.$route.query.direct_name}`;
        }

        let state = "&state={0}";
        if (prames) {
          state = "&state={0}_{1}";
          stateUrl = state.format(prames, 'provider:' + samlAccount.provider);
        } else {
          stateUrl = state.format('provider:' + samlAccount.provider);
        }
      } else {
        const directName = this.$route.query?.direct_name ?? null;
        const unitID = this.$route.query?.unit_id ?? null;
        let state = "&state={0}_{1}";

        if (directName) {
          let params = null;
          if (directName === "menu" || directName === 'soldVehicleSystemTable') {
            params = directName;
          } else if (directName === "systemInformation") {
            params = `${directName}_${unitID}_${this.$route.query.pfc_id}`;
          }
          stateUrl = state.format(params, 'provider:' + samlAccount.provider);
        } else {
          state = "&state={0}";
          stateUrl = state.format('provider:' + samlAccount.provider);
        }

        // CCからSSM5を起動した場合
        if (this.$route.query.ccid) {
          state = "&state={0}_{1}_{2}";
          stateUrl = state.format(this.isSkipStart, this.cloudConnectorId, 'provider:' + samlAccount.provider);
        }
      }
      return stateUrl;
    },
  },

  async created() {
    // 管理画面か実行画面化を判定
    this.isManage = window.location.href.includes("manage");
    // 技本 URL の場合は画面を操作させない
    if (!this.isManage) {
      const host = window.location.hostname;
      this.processing = host && host.indexOf('gihon') !== -1;
    }
    // provider情報を設定
    this.setCurrentProvider(saml.getProviderFromUrl(window.location.href));

    // 初期アクセス時は this.currentProvider が SBR なので、CloudFront のログイン画面を表示する
    // エラーの時、SBRとSUBARU-TECH以外は独自のエラー画面を表示する
    if (process.env.VUE_APP_NEEDS_SSO_SELECTION && (this.currentProvider !== config.SBR_DOMAIN && this.currentProvider !== config.SBR_TECH_DOMAIN)) {
      this.isCloudfrontLoginPage = false;
    }

    // SSM5内部エラーの場合、SSO連携ユーザーはログイン画面に戻る（リダイレクトはしない)
    // CloudFrontは初回アクセス時のみログイン画面を表示する
    // タイムアウト以外でログイン画面にきた場合は自動連係しない（タイムアウト時はすべて自動連係する）
    if (util.parseBoolean(this.$route.query.isSkip) || (util.isCloudFrontUrl() && !this.$route.query.isSkip)) {
      // STG2,Oud4 はSSM5ログイン画面を表示せず、IDP画面に直接遷移
      if (process.env.VUE_APP_BUILD_ENVIRONMENT !== "stg2" && process.env.VUE_APP_BUILD_ENVIRONMENT !== "oud4") {
        this.redirectSkip = true;
        this.displayLoginScreen = true;
      }
    }
    await this.init();
    const lang = util.getWindowNavigatorLanguage(config.LANGUAGE_LIST);
    const switchSsoLanguage = resource.ssoLanguageDisplay(lang, this.currentProvider);
    if (switchSsoLanguage) {
      this.ssoDisplayLanguage = switchSsoLanguage.ssoDisplayLanguage;
    }
  },

  mounted() {
    session.stopSessionTimer();

    // 遷移前に表示していたモーダルを閉じる
    this.$emit('closeAppAllModal');

    // ログイン画面表示時に画面サイズを更新する 
    this.$emit('updateClientHeight');

    // SAML連携のログインのエラーチェック
    if (this.$route.query && (Object.prototype.hasOwnProperty.call(this.$route.query, 'error_description') || Object.prototype.hasOwnProperty.call(this.$route.query, 'error'))) {

      // DOMの再描画を待ってからエラー画面を表示する 
      const internalThis = this;
      this.$nextTick(() => {
        // エラーコードが付与されている場合は連携失敗としてダイアログを表示する。その後サインアウト処理を行う
        const messageErrorDescription = this.$route?.query['error_description'];
        alert.showError(internalThis, null, internalThis.$t('message.external_signin_error', [messageErrorDescription]),
          () => {
            // SUBARU独自ユーザー以外はログイン画面を表示
            if (process.env.VUE_APP_NEEDS_SSO_SELECTION || this.currentProvider !== config.SBR_DOMAIN) {
              this.displayLoginScreen = true;
            }
            this.logout(this);
          });
      });
    }

  },

  beforeRouteEnter(to, from, next) {
    // CITS内の subscriber と gihon は管理画面のページを除外する。
    const subscriberUrl = `subscriber-${process.env.VUE_APP_BUILD_ENVIRONMENT}.ssm.sbr-dev.com`;
    const gihonUrl = `gihon-${process.env.VUE_APP_BUILD_ENVIRONMENT}.ssm-dev.com`;
    const forbiddenPaths = [process.env.VUE_APP_MANAGE_PAGE_SUBSCRIBER_LOGIN, process.env.VUE_APP_MANAGE_PAGE_TECH_HQ_LOGIN, subscriberUrl, gihonUrl];
    const hostname = window.location.href || '';
    if (!util.isSupportBrowser()) {
      next({ path: '/old-browser' });
    } else if (hostname.includes("manage") && forbiddenPaths.some(p => hostname.indexOf(p) !== -1)) {
      next({ path: '/page-not-found' });
    } else {
      next();
    }
  },

};
